import type { ReactNode } from "react";
import { View, type StyleProp, type ViewStyle } from "react-native";

// <TouchLock> — freezes a subtree while `locked`: no touch reaches it and a
// screen reader skips over it, but it stays fully VISIBLE and UNDIMMED. The
// athlete still reads the set they just logged for the whole rest countdown;
// they simply can't edit it until the rest ends (LG-29). Undimmed on purpose —
// Antenore's ask is "lo vedo, UI bloccata": dimming the block to signal the
// lock would eat the contrast on the very numbers he wants to read (the same
// alpha trap the paused rest readout hit, pausedReadoutContrast.test.ts).
//
// It is NOT `editLocked` (a per-control, dim-and-explain gate that forbids
// STRUCTURAL edits on a saved past session while leaving value edits live) and
// NOT IconButton's `locked` (dim-but-tappable, opens a tip). This is a blanket,
// transient, silent freeze of an in-flow region — the thing editLocked
// deliberately leaves live (weight/reps/AMRAP chip) is exactly what this must
// stop, for the 2-3s the countdown runs, because editing a just-logged set
// mid-rest is the LG-26 data-loss (next/archive/backlog.md LG-26/LG-29).
//
// pointerEvents blocks touch; accessibilityElementsHidden (iOS) +
// importantForAccessibility (Android) keep assistive tech from offering
// controls that would silently do nothing. RN-portable (bare View + a11y
// props), no theme/hook dependency, so it rides to web-rnw unchanged.
//
// ⚠️ pointerEvents="none" blocks NEW touches but does NOT blur an already
// focused TextInput — a soft keyboard open on weight/reps keeps typing into the
// frozen field. Callers that freeze over live inputs must call dismissSoftKeyboard() (./dismissSoftKeyboard)
// when `locked` flips true; TouchLock can't reach the focused input from here.
export function TouchLock({
  locked,
  style,
  children,
}: {
  locked: boolean;
  style?: StyleProp<ViewStyle>;
  children: ReactNode;
}) {
  return (
    <View
      style={style}
      pointerEvents={locked ? "none" : "auto"}
      accessibilityElementsHidden={locked}
      importantForAccessibility={locked ? "no-hide-descendants" : "auto"}
    >
      {children}
    </View>
  );
}
